/**
 * Session lifecycle — start, end, and the last chance to send anything.
 *
 * @module logging/instrumentation/session
 */

import { sanitizePath, sanitizeReferrer } from '../core/sanitize';
import { getSessionEngagedMs, isNewSession } from '../core/session';
import { telemetry } from '../telemetry';
import { currentPageDwellMs } from './page-views';

/**
 * Emit `web_session_started` if this page load began the session, and
 * register the handlers that close it out.
 *
 * ## Why `visibilitychange`, and why `pagehide` as well
 *
 * `visibilitychange -> hidden` is the last event a browser reliably fires
 * before a tab is closed, backgrounded, or the OS reclaims it. `unload` and
 * `beforeunload` are not reliable — they are skipped entirely by the
 * back/forward cache on mobile Safari and Chrome, which is precisely where
 * most visits end.
 *
 * `pagehide` is registered too, but only to flush, never to emit. Some
 * browsers fire one, some the other, and some both; flushing twice costs an
 * empty queue check, whereas emitting twice would inflate session counts.
 *
 * A visitor who switches tabs and comes back produces more than one
 * `web_session_ended`. That is recorded in the catalog rather than
 * suppressed here: each one carries the duration so far, so the largest per
 * session is the session length. Suppressing repeats would mean the common
 * case — a visitor who never returns to the tab — silently loses its end
 * event.
 */
export function initSessionInstrumentation(): void {
  if (isNewSession()) {
    telemetry.emit('web_session_started', {
      entry_path: sanitizePath(window.location.pathname),
      referrer: sanitizeReferrer(document.referrer, window.location.hostname),
    });
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') return;

    /* `dwell_ms` is carried here because this is the only event the exit
       page ever appears on — there is no later `web_page_viewed` to report
       the time spent on it. Read before the flush, while the visible clock
       still has the run open. */
    const pageDwellMs = currentPageDwellMs();

    telemetry.emit('web_session_ended', {
      duration_ms: getSessionEngagedMs(),
      ...(pageDwellMs === null ? {} : { dwell_ms: pageDwellMs }),
    });
    // Emit then flush, in that order, so the event just queued travels in
    // the same request rather than waiting for a next time that may not come.
    telemetry.flush('hidden');
  });

  window.addEventListener('pagehide', () => {
    telemetry.flush('hidden');
  });
}
